import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...entradas: ClassValue[]): string {
  return twMerge(clsx(entradas));
}

/**
 * Ordenação natural alfanumérica: "PP-10" vem depois de "PP-9".
 * O endereçamento do PBS é texto livre, então comparação binária erraria a
 * ordem física dos corredores.
 */
const colator = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

export function compararNatural(a: string, b: string): number {
  return colator.compare(a, b);
}

const fmtInteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const fmtDecimal = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function numero(valor: number | null | undefined): string {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return '—';
  return fmtInteiro.format(valor);
}

export function percentual(valor: number | null | undefined): string {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return '—';
  return `${fmtDecimal.format(valor)}%`;
}

/**
 * Reais por extenso — para o número grande, onde o centavo importa menos que
 * a ordem de grandeza ser inconfundível.
 */
const fmtMoeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
});

export function moeda(valor: number | null | undefined): string {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return '—';
  return fmtMoeda.format(valor);
}

/** `183939.26` -> `R$ 184 mil`. Para eixo de gráfico, onde o espaço é o limite. */
export function moedaCompacta(valor: number | null | undefined): string {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return '—';
  if (Math.abs(valor) < 1000) return fmtMoeda.format(valor);
  if (Math.abs(valor) < 1_000_000) return `R$ ${fmtInteiro.format(valor / 1000)} mil`;
  return `R$ ${fmtDecimal.format(valor / 1_000_000)} mi`;
}

export function compacto(valor: number | null | undefined): string {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return '—';
  if (Math.abs(valor) < 1000) return fmtInteiro.format(valor);
  if (Math.abs(valor) < 1_000_000) return `${fmtDecimal.format(valor / 1000)} mil`;
  return `${fmtDecimal.format(valor / 1_000_000)} mi`;
}

/**
 * Data "só data" (YYYY-MM-DD) vira Date no fuso LOCAL, não em UTC.
 *
 * `new Date('2026-09-11')` é meia-noite UTC. Formatado em Brasília (UTC−3)
 * isso vira 10/09 — um dia a menos. Era o motivo de o painel dizer
 * "posições até 10/09" no dia 12/09, com fechamento de 11/09, e de todo o
 * eixo do gráfico de 30 dias estar deslocado.
 *
 * Strings com hora (ISO completo) continuam pelo parser nativo: ali o fuso
 * faz parte da informação e a conversão é correta.
 */
function paraDataLocal(iso: string): Date {
  const so = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!so) return new Date(iso);
  return new Date(Number(so[1]), Number(so[2]) - 1, Number(so[3]));
}

export function dataCurta(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = paraDataLocal(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function dataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function plural(n: number, singular: string, pluralForma: string): string {
  return n === 1 ? singular : pluralForma;
}

/** Agrupa preservando a ordem de aparição das chaves. */
export function agruparPor<T, K extends string>(
  itens: readonly T[],
  chave: (item: T) => K,
): Map<K, T[]> {
  const mapa = new Map<K, T[]>();
  for (const item of itens) {
    const k = chave(item);
    const lista = mapa.get(k);
    if (lista) lista.push(item);
    else mapa.set(k, [item]);
  }
  return mapa;
}

export function limitar(valor: number, minimo: number, maximo: number): number {
  return Math.min(maximo, Math.max(minimo, valor));
}

/** Nome de fornecedor sempre em caixa alta: o XML traz "Atlas Industria", o ERP "ATLAS INDUSTRIA". */
export function caixaAlta<T extends string | null | undefined>(nome: T): T {
  return (nome == null ? nome : nome.replace(/\s+/g, ' ').trim().toLocaleUpperCase('pt-BR')) as T;
}

/** "WHIRLPOOL S.A. - SP": a UF separa as entidades do mesmo fornecedor (a Whirlpool tem SC, SP e AM). */
export function nomeFornecedor(nome: string | null | undefined, uf?: string | null): string | null {
  const n = caixaAlta(nome);
  return n ? (uf ? `${n} - ${uf}` : n) : null;
}
